import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { AlertTriangle, CheckCircle, Sparkles, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '@/stores';
import { selectSelectedAgent } from '@/stores/selectors/agent-selectors';
import { selectCurrentThreadId } from '@/stores/selectors/chat-selectors';
import { selectUserPreferences } from '@/stores/selectors/user-selectors';
import { appConfig } from '@/config/app-config';
import { STORAGE_KEYS } from '@/config/storage-config';
import { PATHS } from '@/config/paths';

// ── Warning Modal ──────────────────────────────────────────────────────────

interface DemoTeamModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const DemoTeamModal: React.FC<DemoTeamModalProps> = ({ isOpen, onClose, onConfirm }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const items = [
    'Team: ACME Hotel – Guest Experience',
    'Users, schedules & escalation policies',
    'Services, event orchestrations & workflows',
    'Business services & dependencies',
    'Test incidents',
  ];

  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', padding: '16px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: '#ffffff', borderRadius: '12px', maxWidth: '480px', width: '100%',
          boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
          display: 'flex', flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '14px 20px', borderBottom: '1px solid #e2e8f0',
          backgroundColor: '#f7fafc', borderTopLeftRadius: '12px', borderTopRightRadius: '12px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <AlertTriangle size={18} color="#D69E2E" />
            <span style={{ fontSize: '16px', fontWeight: 600, color: '#1a202c' }}>
              Create demo team in PagerDuty?
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', color: '#718096',
              padding: '4px', display: 'flex', alignItems: 'center', borderRadius: '4px',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#e2e8f0')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <p style={{ margin: 0, fontSize: '14px', color: '#4a5568' }}>
            This will create real resources in your live PagerDuty instance:
          </p>
          <ul style={{ margin: 0, paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {items.map((item) => (
              <li key={item} style={{ fontSize: '13px', color: '#718096' }}>{item}</li>
            ))}
          </ul>
          <div style={{
            backgroundColor: '#fffbeb', border: '1px solid #f6e05e',
            borderRadius: '8px', padding: '10px 12px',
          }}>
            <p style={{ margin: 0, fontSize: '12px', color: '#744210' }}>
              After creation a <strong>new session</strong> will start automatically so you can select and analyze the demo team right away.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 20px', borderTop: '1px solid #e2e8f0',
          display: 'flex', justifyContent: 'flex-end', gap: '8px',
        }}>
          <button
            onClick={onClose}
            style={{
              fontSize: '14px', fontWeight: 500, padding: '6px 16px', borderRadius: '6px',
              border: '1px solid #e2e8f0', backgroundColor: '#ffffff', color: '#4a5568', cursor: 'pointer',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f7fafc')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#ffffff')}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={{
              fontSize: '14px', fontWeight: 500, padding: '6px 16px', borderRadius: '6px',
              border: 'none', backgroundColor: '#06AC38', color: '#ffffff', cursor: 'pointer',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#059030')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#06AC38')}
          >
            Yes, create it
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ── Section Component ──────────────────────────────────────────────────────

export const DemoTeamSection: React.FC = () => {
  const navigate = useNavigate();
  const selectedAgent = useAppSelector(selectSelectedAgent);
  const currentThreadId = useAppSelector(selectCurrentThreadId);
  const preferences = useAppSelector(selectUserPreferences);
  const agentId = selectedAgent?.id || 'optimize-clean-up-agent';

  // Default true — only hide if explicitly set to false
  if (preferences?.show_demo_section === false) return null;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setIsModalOpen(false);
    const threadId = currentThreadId || sessionStorage.getItem('THREAD_ID');
    if (!threadId) { setError('No active thread — start a conversation first'); return; }

    setIsLoading(true);
    setError(null);
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN) || '';

    try {
      const res = await fetch(
        `${appConfig.API_BASE_URL}/agents/${agentId}/threads/${threadId}/create-demo-team`,
        { method: 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) } }
      );
      const data = await res.json();
      if (res.ok && data.success) {
        setIsDone(true);
        setTimeout(() => {
          navigate(PATHS.AssistantAgent.replace(':agentId', agentId) + '?new=1');
          setTimeout(() => setIsDone(false), 800);
        }, 1500);
      } else {
        setError(data.message || 'Creation failed');
      }
    } catch {
      setError('Network error — please try again');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div>
        <p style={{ margin: '0 0 8px', fontSize: '11px', fontWeight: 600, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          Demo
        </p>
        <div style={{ border: '1px solid #9ae6b4', borderRadius: '8px', backgroundColor: '#f0fff4', padding: '10px 12px' }}>
          {isDone ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <CheckCircle size={14} color="#22C55E" />
                <span style={{ fontSize: '12px', fontWeight: 600, color: '#276749' }}>Demo team created!</span>
              </div>
              <span style={{ fontSize: '12px', color: '#38a169' }}>Starting new session…</span>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Sparkles size={13} color="#22C55E" />
                <span style={{ fontSize: '12px', fontWeight: 600, color: '#276749' }}>ACME Hotel – Guest Experience</span>
              </div>
              <p style={{ margin: 0, fontSize: '12px', color: '#38a169' }}>
                Pre-configured with users, schedules, EPs, and services with intentional issues.
              </p>
              <button
                disabled={isLoading}
                onClick={() => setIsModalOpen(true)}
                style={{
                  fontSize: '12px', fontWeight: 500, padding: '5px 10px', borderRadius: '6px',
                  border: 'none', backgroundColor: isLoading ? '#9ae6b4' : '#06AC38',
                  color: '#ffffff', cursor: isLoading ? 'not-allowed' : 'pointer', alignSelf: 'flex-start',
                }}
              >
                {isLoading ? 'Creating…' : 'Create demo team'}
              </button>
              {error && <p style={{ margin: 0, fontSize: '11px', color: '#e53e3e' }}>{error}</p>}
            </div>
          )}
        </div>
      </div>

      <DemoTeamModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleConfirm}
      />
    </>
  );
};
